import { useState } from 'react';
import { C, mono } from '../theme';
import { Pill } from '../components/Pill';
import { Register } from '../components/Register';
import { LedgerFilter } from '../components/LedgerFilter';
import { Loading, Empty } from '../components/States';
import { api } from '../api';
import type { LedgerScope, MyLedger } from '../api';
import { useAsync } from '../useAsync';
import { inr } from '../format';
import { buildRegister } from '../register';
import { Row } from './Row';

/**
 * Tenant statement (GET /my/ledger) — the same running-balance register the owner
 * sees, read-only. Charges add to the balance; the TDS the tenant deducts and each
 * payment reduce it. Figures are net of that TDS. Scoped by an FY chip row.
 */
export function TenantStatement() {
  const [scope, setScope] = useState<LedgerScope>({}); // {} → current FY (default)
  const { data, loading, error, reload } = useAsync<MyLedger>(() => api.myLedger(scope), [scope]);

  if (loading && !data) return <Loading label="Loading statement…" minHeight={300} />;
  if (error || !data) {
    return (
      <Empty icon="ledger" title="Statement unavailable" hint="Please try again in a moment." minHeight={300}>
        <button onClick={reload} style={retryBtn}>Retry</button>
      </Empty>
    );
  }

  const outstanding = data.closingBalance ?? data.balanceOutstanding ?? 0;
  const tdsRow = data.periods.find((p) => p.tdsAmount > 0 && p.rent > 0);
  const tdsRatePct = tdsRow ? Math.round((tdsRow.tdsAmount / tdsRow.rent) * 100) : 10;
  const register = buildRegister({
    periods: data.periods,
    payments: data.payments,
    openingBalance: data.openingBalance,
    tdsRatePct,
  });

  return (
    <>
      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: '12px 14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 style={{ margin: 0, fontSize: 14, color: C.ink, fontWeight: 650 }}>Balance</h4>
          {outstanding > 0 ? <Pill kind="over">{inr(outstanding)} due</Pill> : <Pill kind="paid">All clear</Pill>}
        </div>
        <div style={{ color: C.muted, fontSize: 12.5, marginTop: 6 }}>
          {data.fyLabel ?? 'This financial year'} · all figures net of the {tdsRatePct}% TDS you deduct.
        </div>
      </div>

      {data.window && data.window.fyOptions.length > 0 && (
        <LedgerFilter fyOptions={data.window.fyOptions} value={scope} onChange={setScope} />
      )}

      {register.length === 0 ? (
        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14, padding: '22px 14px', color: C.muted, fontSize: 13, textAlign: 'center' }}>
          No entries in this period.
        </div>
      ) : (
        <Register entries={register} compact />
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 12.5, color: C.muted }}>
        <span style={{ ...mono() }}>Closing balance · {inr(outstanding)}</span>
      </div>

      <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 14 }}>
        <Row icon="rupee" tone="ac" t="Deposit held" s="Refundable on exit" r={inr(data.depositAmount)} />
      </div>
    </>
  );
}

const retryBtn: React.CSSProperties = {
  border: `1px solid ${C.line}`,
  background: C.card,
  color: C.accent,
  fontWeight: 600,
  fontSize: 13,
  padding: '8px 16px',
  borderRadius: 9,
  cursor: 'pointer',
};
